<template>
  <div class="max-w-5xl mx-auto space-y-8">
    <!-- 页面标题和操作按钮 -->
    <div class="flex items-center justify-between">
      <h2 class="text-2xl text-gray-900">个人信息</h2>
      <el-button 
        v-if="canReportError"
        type="default" 
        class="text-red-600 border-red-300 hover:bg-red-600 hover:text-white hover:border-red-600"
        @click="isDialogOpen = true"
      >
        信息有误
      </el-button>
    </div>

    <!-- 基本信息 -->
    <div class="space-y-6">
      <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2">基本信息</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label class="block mb-2 text-gray-600">工号</label>
          <div class="text-gray-900 py-2">{{ teacherData.teacher_id }}</div>
        </div>
        <div>
          <label class="block mb-2 text-gray-600">姓名</label>
          <div class="text-gray-900 py-2">{{ teacherData.teacher_name }}</div>
        </div>
        <div>
          <label class="block mb-2 text-gray-600">性别</label>
          <div class="text-gray-900 py-2">{{ teacherData.gender }}</div>
        </div>
        <div>
          <label class="block mb-2 text-gray-600">出生日期</label>
          <div class="text-gray-900 py-2">{{ formatDate(teacherData.birth_date) }}</div>
        </div>
      </div>
    </div>

    <!-- 职务信息 -->
    <div class="space-y-6">
      <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2">职务信息</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label class="block mb-2 text-gray-600">职称</label>
          <div class="text-gray-900 py-2">{{ teacherData.teacher_title }}</div>
        </div>
        <div>
          <label class="block mb-2 text-gray-600">当前状态</label>
          <div class="text-gray-900 py-2">{{ teacherData.t_status }}</div>
        </div>
        <div>
          <label class="block mb-2 text-gray-600">角色</label>
          <div class="text-gray-900 py-2">{{ teacherData.role }}</div>
        </div>
        <div>
          <label class="block mb-2 text-gray-600">入职时间</label>
          <div class="text-gray-900 py-2">
            {{ formatDate(teacherData.entry_time) }}
            <span class="text-gray-500 text-sm ml-2">(工龄: {{ calculateYearsOfService(teacherData.entry_time) }})</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 学历学位信息 -->
    <div class="space-y-6">
      <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2">学历学位信息</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label class="block mb-2 text-gray-600">最高学历</label>
          <div class="text-gray-900 py-2">{{ teacherData.education }}</div>
        </div>
        <div>
          <label class="block mb-2 text-gray-600">最高学位</label>
          <div class="text-gray-900 py-2">{{ teacherData.degree }}</div>
        </div>
      </div>
    </div>

    <!-- 所属部门信息 -->
    <div class="space-y-6">
      <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2">所属部门信息</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label class="block mb-2 text-gray-600">所属学院</label>
          <div class="text-gray-900 py-2">{{ teacherData.college }}</div>
        </div>
        <div>
          <label class="block mb-2 text-gray-600">所属教研室</label>
          <div class="text-gray-900 py-2">{{ teacherData.department }}</div>
        </div>
        <div v-if="teacherData.director_name">
          <label class="block mb-2 text-gray-600">教研室主任</label>
          <div class="text-gray-900 py-2">{{ teacherData.director_name }}</div>
        </div>
      </div>
    </div>

    <!-- 联系方式 -->
    <div class="space-y-6">
      <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2">联系方式</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label class="block mb-2 text-gray-600">联系电话</label>
          <div class="text-gray-900 py-2">{{ teacherData.phone }}</div>
        </div>
        <div>
          <label class="block mb-2 text-gray-600">电子邮箱</label>
          <div class="text-gray-900 py-2">{{ teacherData.email }}</div>
        </div>
      </div>
    </div>

    <!-- 提示信息 -->
    <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <p class="text-blue-800 text-sm">
        <span class="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
        如发现信息有误，请点击右上角"信息有误"按钮进行反馈，我们会及时处理。
      </p>
    </div>

    <!-- 信息有误对话框 -->
    <el-dialog v-model="isDialogOpen" title="反馈信息有误" width="600px">
      <div class="space-y-6">
        <p class="text-gray-600 text-sm">请选择有误的信息字段，并补充说明，我们会尽快处理</p>
        <!-- 错误字段选择 -->
        <div>
          <label class="block mb-3 text-gray-900">请选择有误的信息（可多选）</label>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <el-checkbox
              v-for="field in errorFields"
              :key="field.id"
              v-model="selectedErrorsMap[field.id]"
              :label="field.label"
              @change="() => handleErrorFieldToggle(field.id)"
            />
          </div>
        </div>

        <!-- 错误描述 -->
        <div>
          <label class="block mb-2 text-gray-900">详细说明（选填）</label>
          <el-input
            type="textarea"
            v-model="errorDescription"
            :rows="4"
            placeholder="请详细描述信息有误的情况，以便我们更快地处理..."
          />
        </div>

        <!-- 提交按钮 -->
        <div class="flex justify-end space-x-3">
          <el-button @click="isDialogOpen = false">取消</el-button>
          <el-button type="danger" class="hover:bg-red-600 hover:text-white hover:border-red-600" :disabled="selectedErrors.length === 0" @click="handleSubmitError">提交反馈</el-button>
        </div>
      </div>
    </el-dialog>
  </div>
  
</template>

<script setup>
import { reactive, ref, computed } from 'vue'
import { useAppStore } from '@/stores'
import { ElMessage } from 'element-plus'
import { onMounted } from 'vue'
import { getMyInfo, submitFeedback } from '../../api/user'

const store = useAppStore()
const canReportError = computed(() => store.hasFunctionByName('申报信息有误'))

// 模拟当前登录教师的数据（对应 ts_teacher 表）
const teacherData = reactive({
  teacher_id: '',
  teacher_name: '',
  teacher_title: '',
  t_status: '',
  gender: '',
  birth_date: '',
  phone: '',
  email: '',
  entry_time: '',
  education: '',
  degree: '',
  college: '',
  department: '',
  role: '',
  director_name: ''
})

// 可选择的错误字段列表
const errorFields = [
  { id: 'teacher_id', label: '工号' },
  { id: 'teacher_name', label: '姓名' },
  { id: 'gender', label: '性别' },
  { id: 'birth_date', label: '出生日期' },
  { id: 'teacher_title', label: '职称' },
  { id: 't_status', label: '当前状态' },
  { id: 'role', label: '角色' },
  { id: 'entry_time', label: '入职时间' },
  { id: 'education', label: '最高学历' },
  { id: 'degree', label: '最高学位' },
  { id: 'college', label: '所属学院' },
  { id: 'department', label: '所属教研室' },
  { id: 'phone', label: '联系电话' },
  { id: 'email', label: '电子邮箱' }
]

// 对话框与错误上报数据
const isDialogOpen = ref(false)
const selectedErrors = ref([])
const errorDescription = ref('')

// 复选框映射，便于 Element Plus 受控
const selectedErrorsMap = reactive(Object.fromEntries(errorFields.map(f => [f.id, false])))

// 日期格式化
const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// 工龄计算
const calculateYearsOfService = (entryTime) => {
  if (!entryTime) return '-'
  const entry = new Date(entryTime)
  const now = new Date()
  const years = now.getFullYear() - entry.getFullYear()
  return `${years}年`
}

// 切换错误字段选择
const handleErrorFieldToggle = (fieldId) => {
  const current = new Set(selectedErrors.value)
  if (current.has(fieldId)) {
    current.delete(fieldId)
  } else {
    current.add(fieldId)
  }
  selectedErrors.value = Array.from(current)
}

// 提交错误信息反馈
const handleSubmitError = async () => {
  if (selectedErrors.value.length === 0) {
    ElMessage.warning('请至少选择一个有误的信息字段')
    return
  }
  const isSelected = (k) => selectedErrors.value.includes(k)
  const payload = {
    nameError: isSelected('teacher_name') ? 1 : 0,
    genderError: isSelected('gender') ? 1 : 0,
    birthDateError: isSelected('birth_date') ? 1 : 0,
    professionalTitleError: isSelected('teacher_title') ? 1 : 0,
    currentStatusError: isSelected('t_status') ? 1 : 0,
    roleError: isSelected('role') ? 1 : 0,
    employmentDateError: isSelected('entry_time') ? 1 : 0,
    highestEducationError: isSelected('education') ? 1 : 0,
    highestDegreeError: isSelected('degree') ? 1 : 0,
    collegeError: isSelected('college') ? 1 : 0,
    departmentError: isSelected('department') ? 1 : 0,
    phoneError: isSelected('phone') ? 1 : 0,
    emailError: isSelected('email') ? 1 : 0,
    description: errorDescription.value || ''
  }
  try {
    await submitFeedback(payload)
    ElMessage.success('已提交信息有误反馈，相关人员将尽快处理')
    Object.keys(selectedErrorsMap).forEach(k => (selectedErrorsMap[k] = false))
    selectedErrors.value = []
    errorDescription.value = ''
    isDialogOpen.value = false
  } catch {
    ElMessage.error('提交反馈失败')
  }
}


onMounted(async () => {
  try {
    const res = await getMyInfo()
    const info = res?.data ?? res
    const genderMap = { 1: '男', 2: '女', 3: '其他' }
    const statusMap = { 1: '在职', 2: '离职', 3: '休假' }
    teacherData.teacher_id = info?.employeeNumber ?? ''
    teacherData.teacher_name = info?.name ?? ''
    teacherData.teacher_title = info?.professionalTitle ?? ''
    teacherData.t_status = info?.statusName ?? statusMap[info?.currentStatus] ?? ''
    teacherData.gender = info?.genderName ?? genderMap[info?.gender] ?? ''
    teacherData.birth_date = info?.birthDate ?? ''
    teacherData.phone = info?.phone ?? ''
    teacherData.email = info?.email ?? ''
    teacherData.entry_time = info?.employmentDate ?? ''
    teacherData.education = info?.highestEducation ?? ''
    teacherData.degree = info?.highestDegree ?? ''
    teacherData.college = info?.college ?? ''
    teacherData.department = info?.department ?? ''
    teacherData.role = info?.role ?? ''
    teacherData.director_name = ''
  } catch {
    ElMessage.error('个人信息加载失败')
  }
})
</script>

<style scoped>
/* 使用 Tailwind CSS 类，无需额外样式 */
</style>
